import { useState } from 'react';
import { Alert, FormDialog, FormField, Textarea } from '@nexvia-logistics/ui';
import { describeStorageError } from '@/shared/storage-api';

/** nexvia-api-web `storage.schemas.ts` `NOTE_MAX`: the note and the reason, both. */
const TEXT_MAX = 1000;

// NEX-1170 (X1) — 'withdrawal': "El cliente retiró sus cosas", a confirmation with no text.
export type ReceptionAction = 'confirm' | 'not-received' | 'withdrawal';

const COPY: Record<
  ReceptionAction,
  { title: string; intro: string; label: string | null; placeholder: string; submit: string; failure: string }
> = {
  confirm: {
    title: 'Confirmar recepción',
    intro: 'Confirmá que recibiste las cosas del cliente en tu depósito. El guardado pasa a activo y le avisamos al cliente.',
    label: 'Nota sobre el estado de las cosas (opcional)',
    placeholder: 'Ej: llegó una caja golpeada, faltaba una silla…',
    submit: 'Sí, las recibí',
    failure: 'No pudimos confirmar la recepción. Intentá de nuevo.'
  },
  'not-received': {
    title: 'No lo recibí',
    intro: 'Contanos qué pasó. Le avisamos a Nexvia para que lo revise; el guardado sigue contratado hasta que recibas las cosas.',
    label: 'Motivo',
    placeholder: 'Ej: el transportista no vino en la fecha acordada…',
    submit: 'Avisar a Nexvia',
    failure: 'No pudimos avisar a Nexvia. Intentá de nuevo.'
  },
  withdrawal: {
    title: 'El cliente retiró sus cosas',
    intro:
      'Confirmá que el cliente ya retiró todas sus cosas de tu depósito. El guardado termina y le avisamos al cliente. No se puede deshacer.',
    label: null,
    placeholder: '',
    submit: 'Sí, las retiró',
    failure: 'No pudimos terminar el guardado. Intentá de nuevo.'
  }
};

interface ReceptionDialogProps {
  action: ReceptionAction;
  /** Gets the trimmed text: the note (`null` when empty) or the reason (never empty). */
  onSubmit: (text: string | null) => Promise<void>;
  onCancel: () => void;
}

/**
 * NEX-1170 (decision A4) — the confirmation step of "Confirmar recepción" (optional note on the
 * state of the things) and "No lo recibí" (required reason, it goes to operations); X1 — and of "El
 * cliente retiró sus cosas" (no text: it ends the guardado). All are irreversible from this panel,
 * hence a dialog rather than a one-click action. The API's 409s
 * (`STORAGE_STATUS_TRANSITION_INVALID`: confirmed meanwhile, by another tab or by support) show
 * here in Spanish, through `describeStorageError`.
 */
export function ReceptionDialog({ action, onSubmit, onCancel }: ReceptionDialogProps) {
  const copy = COPY[action];
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = text.trim();
  const missingReason = action === 'not-received' && !trimmed;

  const handleSubmit = async () => {
    if (missingReason) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(trimmed || null);
    } catch (err) {
      setError(describeStorageError(err, copy.failure));
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open
      onClose={onCancel}
      onSubmit={handleSubmit}
      title={copy.title}
      submitLabel={copy.submit}
      cancelLabel="Cancelar"
      submitting={submitting}
      submitDisabled={missingReason}
    >
      <p className="m-0 text-ui-body text-on-surface-variant">{copy.intro}</p>

      {error && <Alert tone="critical">{error}</Alert>}

      {copy.label && (
        <FormField label={copy.label} counter={{ value: text.length, max: TEXT_MAX }}>
          {(field) => (
            <Textarea
              {...field}
              value={text}
              onChange={(event) => setText(event.target.value)}
              maxLength={TEXT_MAX}
              rows={3}
              placeholder={copy.placeholder}
            />
          )}
        </FormField>
      )}
    </FormDialog>
  );
}
